<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div class="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md relative">
      <button class="absolute bg-transparent  border-0 outline-none top-2 right-4 text-gray-700 hover:text-black text-2xl admin-button--ghost admin-button--icon" aria-label="Schließen" title="Schließen" @click="$emit('close')">
        <i class="fa fa-xmark"></i>
      </button>
      <h2 class="text-xl font-bold mb-4">Dokument hochladen</h2>
      <form @submit.prevent="submitDocument" class="relative">
        <div class="mb-4">
          <label class="block font-semibold mb-1">Kategorie</label>
          <select v-model="category" @change="categoryChanged" class="border rounded px-3 py-2 w-full" required>
            <option v-for="option in categories" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>
        <div class="mb-4" v-if="category === 'Qualifikation'">
          <label class="block font-semibold mb-1">Qualifikation</label>
          <select v-model="subcategory" @change="subcategoryChanged" class="border rounded px-3 py-2 w-full" required>
            <option v-for="option in qualifications" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div v-if="requiresValidity" class="mb-4">
          <label class="block font-semibold mb-1">Gültig bis</label>
          <input v-model="validUntil" type="date" class="border rounded px-3 py-2 w-full" required />
        </div>
        <div class="mb-4">
          <label class="block font-semibold mb-1">Datei</label>
          <div
            class="border-2 border-dashed border-gray-400 rounded px-3 py-6 w-full text-center cursor-pointer mb-2 transition-colors"
            :class="{ 'bg-blue-50 border-blue-400': isDragging }"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
          >
            <span v-if="!file">Datei hierher ziehen oder klicken, um auszuwählen</span>
            <span v-else>{{ file.name }}</span>
          </div>
          <input type="file" ref="fileInput" @change="onFileChange" required class="hidden" />
          <label v-if="category !== 'Qualifikation'" class="block mt-2 mb-1 font-medium">Dateiname</label>
          <input v-if="category !== 'Qualifikation'"
            type="text"
            v-model="fileName"
            class="w-full border rounded px-2 py-1 mb-2"
            :disabled="!file"
            required
          />
        </div>
        
        <div class="mb-4 flex items-center">
          <input id="visibleToUser" type="checkbox" v-model="visibleToUser" class="mr-2" />
          <label for="visibleToUser" class="font-semibold">Dem Nutzer anzeigen</label>
        </div>
        <div class="mb-6 flex items-center">
          <input id="restricted" type="checkbox" v-model="restricted" class="mr-2" />
          <label for="restricted" class="font-semibold">Vertraulich</label>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" class="bg-gray-300 text-gray-700 rounded px-4 py-2 admin-button--secondary" @click="$emit('close')">Abbrechen</button>
          <button type="submit" class="bg-blue-500 text-white rounded px-4 py-2 admin-button--primary" :disabled="!file">Hochladen</button>
        </div>
        <div v-if="loading" class="spinnerwrapper">
            <div class="spinner"></div><br>
            <span class="text-white font-bold">Upload läuft, Restzeit: {{ remaining }} Sekunden</span>
          </div>
      </form>
    </div>
  </div>
</template>

<script>
import CASP from '@/CASP';
export default {
  name: 'DocumentUploadModal',
  props: {
    localUser: {
      type: Object,
      required: true
    },
    initialSubcategory: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      remaining: 0,
      file: null,
      fileName: '',
      isDragging: false,
      category: this.initialSubcategory ? 'Qualifikation' : 'Personalakte',
      categories: ['Personalakte', 'Qualifikation'],
      subcategory: this.initialSubcategory,
      validUntil: '',
      visibleToUser: false,
      restricted: false,
      interval: null,
      qualifications: [
        { label: 'IHK-Bescheinigung', value: 'IHK-Bescheinigung' },
        { label: 'Bewacher-PDF', value: 'Bewacher-PDF' },
        { label: 'Aufenthaltserlaubnis', value: 'Aufenthaltsnachweis' },
        { label: 'Personalausweis', value: 'Personalausweis' },
        { label: 'SV-Nachweis', value: 'SV-Nachweis' }
      ]
    };
  },
  computed: {
    requiresValidity() {
      return this.category === 'Qualifikation' &&
        ['Aufenthaltsnachweis', 'Personalausweis'].includes(this.subcategory);
    }
  },
  methods: {
    categoryChanged() {
      if (this.category !== 'Qualifikation') {
        this.subcategory = '';
        this.validUntil = '';
      }
    },
    subcategoryChanged() {
      if (!this.requiresValidity) this.validUntil = '';
    },
    onFileChange(e) {
      this.file = e.target.files[0] || null;
      if (this.file) {
        this.fileName = this.file.name;
      } else {
        this.fileName = '';
      }
      this.isDragging = false;
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(e) {
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        this.file = droppedFiles[0];
        this.fileName = this.file.name;
      }
      this.isDragging = false;
    },
    submitDocument() {
      if (!this.file || !this.category ) return;
      if (this.category === 'Qualifikation' && !this.subcategory) return;
      if (this.requiresValidity && !this.validUntil) return;
      let fileEnding = this.file.name.split('.').pop();
      if(this.category === 'Qualifikation') this.fileName = this.subcategory + '.' + fileEnding;
      const reader = new FileReader();
      reader.onload = (event) => {
        this.loading = true;
        this.remaining = Math.ceil((this.file.size / 52428800) * 10);
        let payload = {
          fileName: this.fileName,
          fileData: event.target.result,
          category: this.category,
          subcategory: this.subcategory,
          validUntil: this.requiresValidity ? this.validUntil : null,
          visibleToUser: this.visibleToUser,
          restricted: this.restricted,
        }
        CASP.addUserDocument(this.localUser.id, payload, (status, response) => {
        if (status == 200) {
          this.$emit('updateUser', this.localUser);
          clearInterval(this.interval);
          this.loading = false;
          this.$emit('close');
        } else {
          clearInterval(this.interval);
          this.loading = false;
          alert(response?.desc || 'Fehler beim Hochladen des Dokuments');
        }
      })
      this.interval = setInterval(() => {
        if (this.remaining <= 0) {
          clearInterval(this.interval);
          this.remaining = "wenige "
        }else{
          this.remaining -= 1;
        }
      }, 1000);      

      };
      reader.readAsDataURL(this.file);
    }
  }
};
</script>

<style scoped>
</style>
